import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Fab, Tooltip, Chip, Box, CircularProgress } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getPanelHomePath } from './superadmin/superAdminHome';

const POS_STORAGE_KEY = 'gts_impersonation_fab_pos';
const MARGIN = 8;
const DEFAULT_RIGHT = 16;
const DEFAULT_BOTTOM = 16;
const DRAG_THRESHOLD = 5;

type FabPos = { right: number; bottom: number };

const readStoredPos = (): FabPos => {
  try {
    const raw = localStorage.getItem(POS_STORAGE_KEY);
    if (!raw) return { right: DEFAULT_RIGHT, bottom: DEFAULT_BOTTOM };
    const p = JSON.parse(raw) as Partial<FabPos>;
    if (typeof p.right === 'number' && typeof p.bottom === 'number') {
      return { right: p.right, bottom: p.bottom };
    }
  } catch {
    /* yok say */
  }
  return { right: DEFAULT_RIGHT, bottom: DEFAULT_BOTTOM };
};

const clampPos = (pos: FabPos, boxW: number, boxH: number): FabPos => {
  const maxR = Math.max(MARGIN, window.innerWidth - boxW - MARGIN);
  const maxB = Math.max(MARGIN, window.innerHeight - boxH - MARGIN);
  return {
    right: Math.min(maxR, Math.max(MARGIN, pos.right)),
    bottom: Math.min(maxB, Math.max(MARGIN, pos.bottom)),
  };
};

/**
 * Impersonation sırasında sağ altta duran "Yöneticiye dön" yüzen çubuğu.
 * Sayfa kontrollerinin üstünü kapatırsa sürüklenip kenara alınabilir; konum hatırlanır.
 */
const ImpersonationFab: React.FC = () => {
  const { isImpersonating, impersonationInfo, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const [returning, setReturning] = useState(false);
  const [pos, setPos] = useState<FabPos>(readStoredPos);
  const [dragging, setDragging] = useState(false);

  const boxRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    startRight: number;
    startBottom: number;
    moved: boolean;
  } | null>(null);
  const suppressClickRef = useRef(false);

  const measureBox = useCallback(() => {
    const el = boxRef.current;
    if (!el) return { w: 320, h: 48 };
    const r = el.getBoundingClientRect();
    return { w: r.width || 320, h: r.height || 48 };
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (dragRef.current || returning) return;
      const drag = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        startRight: pos.right,
        startBottom: pos.bottom,
        moved: false,
      };
      dragRef.current = drag;

      const onMove = (ev: PointerEvent) => {
        if (ev.pointerId !== drag.pointerId) return;
        const deltaX = drag.startX - ev.clientX;
        const deltaY = drag.startY - ev.clientY;
        if (!drag.moved && Math.abs(deltaX) < DRAG_THRESHOLD && Math.abs(deltaY) < DRAG_THRESHOLD) {
          return;
        }
        drag.moved = true;
        setDragging(true);
        const { w, h } = measureBox();
        setPos(clampPos({ right: drag.startRight + deltaX, bottom: drag.startBottom + deltaY }, w, h));
      };

      const onEnd = (ev: PointerEvent) => {
        if (ev.pointerId !== drag.pointerId) return;
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onEnd);
        window.removeEventListener('pointercancel', onEnd);
        dragRef.current = null;
        setDragging(false);
        if (drag.moved) {
          suppressClickRef.current = true;
          setTimeout(() => {
            suppressClickRef.current = false;
          }, 0);
          const { w, h } = measureBox();
          const next = clampPos(
            {
              right: drag.startRight + (drag.startX - ev.clientX),
              bottom: drag.startBottom + (drag.startY - ev.clientY),
            },
            w,
            h
          );
          setPos(next);
          try {
            localStorage.setItem(POS_STORAGE_KEY, JSON.stringify(next));
          } catch {
            /* yalnızca oturumda kalır */
          }
        }
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onEnd);
      window.addEventListener('pointercancel', onEnd);
    },
    [measureBox, pos.bottom, pos.right, returning]
  );

  useEffect(() => {
    const onResize = () => {
      const { w, h } = measureBox();
      setPos((prev) => clampPos(prev, w, h));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [measureBox]);

  if (!isImpersonating) return null;

  const targetName =
    [impersonationInfo?.targetUser?.firstName, impersonationInfo?.targetUser?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim() ||
    impersonationInfo?.targetUser?.email ||
    'kullanıcı';

  const handleReturn = async () => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    setReturning(true);
    try {
      await stopImpersonation();
      navigate(getPanelHomePath('superadmin'));
    } catch (e) {
      console.error('[ImpersonationFab] Yöneticiye dönüş hatası:', e);
    } finally {
      setReturning(false);
    }
  };

  return (
    <Box
      ref={boxRef}
      onPointerDown={handlePointerDown}
      sx={{
        position: 'fixed',
        right: pos.right,
        bottom: pos.bottom,
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        pointerEvents: 'auto',
        touchAction: 'none',
        userSelect: 'none',
        cursor: dragging ? 'grabbing' : 'grab',
      }}
    >
      <Chip
        icon={<DragIndicatorIcon />}
        label={`${targetName} olarak görüntülüyorsunuz`}
        color="warning"
        size="small"
        sx={{
          bgcolor: 'rgba(237, 108, 2, 0.92)',
          color: 'white',
          fontWeight: 600,
          boxShadow: 3,
          cursor: dragging ? 'grabbing' : 'grab',
          '& .MuiChip-icon': { color: 'white', ml: 0.25 },
          display: { xs: 'none', sm: 'flex' },
        }}
      />
      <Tooltip title="Yöneticiye (süper admin) dön — sürükleyerek taşıyabilirsiniz">
        <Fab
          size="small"
          color="warning"
          onClick={handleReturn}
          disabled={returning}
          sx={{ color: 'white', cursor: dragging ? 'grabbing' : 'grab' }}
          aria-label="Yöneticiye dön"
        >
          {returning ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <LogoutIcon />}
        </Fab>
      </Tooltip>
    </Box>
  );
};

export default ImpersonationFab;
